import { Button } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import Chartjs from 'chart.js/auto';
import MoveDownIcon from '@mui/icons-material/MoveDown';
import { useTrans } from "~/hooks/useTrans";

import "./style.less";

export interface IBarGraphItem {
	label: string;
	values: Array<number>;
	color?: string;
}

interface IBarGraph {
	title?: string;
	subTitle?: string;
	columns: Array<string>;
	items: Array<IBarGraphItem>;
	height?: number;
	getYAxe?: (number: string) => string;
}

export default function BarGraph({
	title = "",
	subTitle = "",
	columns = [],
	items = [],
	height = 0,
	getYAxe,
}: IBarGraph) {
	const { trans } = useTrans();
	const chartContainer = useRef(null);
	const [chartInstance, setChartInstance] = useState(null);

	const data = {
		labels: columns,
		datasets: items.map(item => ({
			label: item.label,
			data: item.values,
			yAxisID: 'y',
		})),
	};
	const options = {
		responsive: true,
		interaction: {
			mode: 'index' as const,
			intersect: false,
		},
		maintainAspectRatio: height === 0,
		stacked: false,
		scales: {
			y: {
				type: 'linear' as const,
				display: true,
				position: 'left' as const,
				ticks: {
					callback: function (value: string) {
						return getYAxe
							? getYAxe(value)
							: value;
					},
				},
			},
		},
	};
	const chartConfig = {
		type: 'bar',
		data,
		options,
	};

	function toggleAllDatasets() {
		for (let ind = 0; ind < items.length; ind++) {
			chartInstance.setDatasetVisibility(ind, !chartInstance.isDatasetVisible(ind));
		}

		chartInstance.update();
		chartInstance.render();
	}

	useEffect(() => {
		if (chartContainer && chartContainer.current && !chartInstance) {
			const newChartInstance = new Chartjs(chartContainer.current, chartConfig);

			setChartInstance(newChartInstance);
		}
	}, [chartContainer, chartInstance]);

	return <div className="barGraph">
		<div className="barGraph__title">
			<span>{ title }</span>
			<Button onClick={toggleAllDatasets} title={trans("graph.switchAllDatasets")}>
				<MoveDownIcon />
			</Button>
		</div>
		{ subTitle && <div className="barGraph__subTitle">
			{ subTitle }
		</div> }
		<div style={{ height: height > 0 ? `${height}px` : "auto" }}>
			<canvas ref={chartContainer} />
		</div>
	</div>;
}
